import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getComboById } from '../../Webservice/offerAPI';
import { API_URL } from '../../Utils/url';
import { 
    Box, Typography, Paper, Grid, Card, CardContent, Divider, Avatar, 
    Breadcrumbs, Link as MuiLink, Button, Chip, CircularProgress 
} from '@mui/material';
import { MdArrowBack, MdEdit } from "react-icons/md";
import { motion } from 'framer-motion';

const calculateComboStock = (products) => {
    if (!products || products.length === 0) return 0;
    let minStock = Infinity;
    products.forEach(item => {
        const p = item.product_id || item;
        const qtyNeeded = item.quantity || 1;
        const available = p.availableQuantity || 0;
        const possibleCombos = Math.floor(available / qtyNeeded);
        if (possibleCombos < minStock) minStock = possibleCombos;
    });
    return minStock === Infinity ? 0 : minStock;
};

const ViewComboOffer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [combo, setCombo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchComboDetails = async () => {
            try {
                const response = await getComboById(id);
                // Adjust based on the actual response structure from getComboById
                if (response && (response.combo || response.data)) {
                    setCombo(response.combo || response.data);
                }
            } catch (error) {
                console.error("Failed to fetch combo details", error);
            } finally {
                setLoading(false);
            }
        };
        fetchComboDetails();
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!combo) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5">Combo not found</Typography>
                <Button onClick={() => navigate('/offers/combo')} sx={{ mt: 2 }} variant="contained">Go Back</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, background: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Mulish, sans-serif' }}>
                        Combo Offer Details
                    </Typography>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                        <MuiLink underline="hover" color="inherit" onClick={() => navigate('/offers/combo')} sx={{ cursor: 'pointer' }}>
                            Combo Offers
                        </MuiLink>
                        <Typography color="text.primary">View Combo</Typography>
                    </Breadcrumbs>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<MdEdit />} 
                        onClick={() => navigate(`/offers/combo/edit/${id}`)}
                        sx={{ background: 'rgb(255 209 2)', color: 'black', fontWeight: 700, borderRadius: '10px' }}
                    >
                        Edit Combo
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<MdArrowBack />} 
                        onClick={() => navigate('/offers/combo')}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e5e7eb', height: '100%' }}>
                        <Typography variant="h6" fontWeight={800} fontFamily="Mulish" mb={2}>
                            Combo Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Combo Title</Typography>
                            <Typography variant="body1" fontWeight={700} color="#1a1c1e">{combo.title}</Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Combo Code</Typography>
                            <Typography variant="body1" fontWeight={700} color="#1a1c1e">{combo.comboCode || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Short Description</Typography>
                            <Typography variant="body2" color="#4b5563">{combo.shortDescription || 'No short description provided.'}</Typography>
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Full Description</Typography>
                            <Typography variant="body2" color="#4b5563">{combo.description || 'No description provided.'}</Typography>
                        </Box>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, background: '#f3f4f6', borderRadius: '8px', textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">MRP</Typography>
                                    <Typography variant="h6" color="#dc2626" fontWeight={800} sx={{ textDecoration: 'line-through' }}>₹{combo.b2c_mrp || 0}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, background: 'rgba(5, 150, 105, 0.1)', borderRadius: '8px', textAlign: 'center' }}>
                                    <Typography variant="caption" color="#059669" fontWeight={700} display="block">Selling Price</Typography>
                                    <Typography variant="h6" color="#059669" fontWeight={800}>₹{combo.b2c_sellingPrice || 0}</Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={combo.isPublished ? "Live" : "Not Live"} color={combo.isPublished ? "success" : "default"} sx={{ fontWeight: 700 }} />
                            <Chip label={combo.isActive ? "Active" : "Inactive"} color={combo.isActive ? "primary" : "default"} sx={{ fontWeight: 700 }} />
                            <Chip 
                                label={calculateComboStock(combo.products) > 0 ? `In Stock (${calculateComboStock(combo.products)})` : 'Out of Stock'} 
                                variant="outlined" 
                                color={calculateComboStock(combo.products) > 0 ? "success" : "error"}
                                sx={{ fontWeight: 700 }} 
                            />
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #e5e7eb', height: '100%' }}>
                        <Typography variant="h6" fontWeight={800} fontFamily="Mulish" mb={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            Included Products
                            <Chip label={`${combo.products?.length || 0} Products`} size="small" sx={{ background: 'rgb(255 209 2)', fontWeight: 800 }} />
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={2}>
                            {combo.products?.map((item, index) => {
                                // Sometimes API returns populated product object in item.product_id, sometimes item is the product
                                const p = item.product_id || item;
                                return (
                                    <Grid item xs={12} sm={6} key={p._id || index}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <Card sx={{ display: 'flex', alignItems: 'center', p: 1.5, border: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                                <Avatar 
                                                    src={p.product_images?.[0] ? (p.product_images[0].startsWith('http') ? p.product_images[0] : `${API_URL.replace(/\/api\/v1\/?$/, '')}${p.product_images[0]}`) : '/img/default-product.png'} 
                                                    variant="rounded" 
                                                    sx={{ width: 60, height: 60, mr: 2 }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body1" fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                                        {p.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        SKU: {p.sku || 'N/A'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                        <Typography variant="body2" fontWeight={700} color="#059669">
                                                            ₹{p.b2c_sellingPrice || p.b2c_mrp || 0}
                                                        </Typography>
                                                        <Chip label={`Qty: ${item.quantity || 1}`} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 800 }} />
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </motion.div>
                                    </Grid>
                                );
                            })}
                            {(!combo.products || combo.products.length === 0) && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                        No products found in this combo.
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ViewComboOffer;
